import { getAccessToken } from './auth';
import { isDesignPreview, previewRequest } from './preview';
import {
  AscoraJobSummary,
  Take5Submission,
  SubmissionResult,
  SwmsRecordSummary,
  M365ProfileResponse,
  JobDetailResponse,
  ConfigResponse,
  AdminConfigKind,
  HazardCategory,
  PpeItem,
  RegCheck,
  RiskDefault,
} from '../types';

const API_BASE = import.meta.env.VITE_API_BASE as string;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (isDesignPreview) return previewRequest<T>(path);
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });

  if (!response.ok) {
    let errorBody: { error?: string; issues?: unknown } = {};
    try { errorBody = await response.json(); } catch { /* ignore */ }
    throw new ApiError(
      errorBody.error || `API request failed (${response.status})`,
      response.status,
      errorBody.issues
    );
  }

  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public issues?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

interface GetJobsResponse {
  success: boolean;
  user: { name: string; email: string };
  jobs: AscoraJobSummary[];
  count: number;
}

export async function getJobs(): Promise<GetJobsResponse> {
  return request<GetJobsResponse>('/jobs');
}

interface GetJobResponse extends JobDetailResponse {
  success: boolean;
}

export async function getJobDetail(jobNumber: string): Promise<JobDetailResponse> {
  const res = await request<GetJobResponse>(`/jobs/${encodeURIComponent(jobNumber)}`);
  return { job: res.job, siteContact: res.siteContact };
}

export async function submitTake5(submission: Take5Submission): Promise<SubmissionResult> {
  return request<SubmissionResult>('/take5/submit', {
    method: 'POST',
    body: JSON.stringify(submission),
  });
}

export async function getJobSwms(jobNumber: string): Promise<SwmsRecordSummary[]> {
  const res = await request<{ success: boolean; records: SwmsRecordSummary[] }>(`/swms/jobs/${encodeURIComponent(jobNumber)}`);
  return res.records;
}

interface GetM365Response extends M365ProfileResponse {
  success: boolean;
}

export async function getM365Profile(): Promise<M365ProfileResponse> {
  const res = await request<GetM365Response>('/m365/profile');
  return { profile: res.profile, supervisorCandidates: res.supervisorCandidates };
}

interface GetConfigResponse extends ConfigResponse {
  success: boolean;
}

export async function getConfig(): Promise<ConfigResponse> {
  const res = await request<GetConfigResponse>('/config');
  return { config: res.config, isAdmin: res.isAdmin };
}

// =============================================================================
// ADMIN — write endpoints. Server enforces admin-only access too.
// =============================================================================

type AnyConfigItem = HazardCategory | PpeItem | RegCheck | RiskDefault;

export async function adminUpsert(kind: AdminConfigKind, item: AnyConfigItem): Promise<void> {
  await request<{ success: boolean }>(`/admin/config/${kind}`, {
    method: 'POST',
    body: JSON.stringify(item),
  });
}

export async function adminDelete(kind: AdminConfigKind, id: string): Promise<void> {
  await request<{ success: boolean }>(`/admin/config/${kind}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
